import { ArtifactActionsButtons } from "@/components/artifacts/ArtifactActionsButtons.tsx";
import { ArtifactErrorBoundary } from "@/components/artifacts/ArtifactErrorBoundary.tsx";
import { ArtifactStatusDate } from "@/components/artifacts/ArtifactStatusDate.tsx";
import { ArtifactView } from "@/components/artifacts/view/ArtifactView.tsx";
import type { ArtifactViewPatcherOverride } from "@/components/artifacts/view/ArtifactViewContext.tsx";
import { Button } from "@/components/ui/button.tsx";
import { errorToast } from "@/components/ui/toast.tsx";
import { artifactQueries } from "@/hooks/artifactQueries.ts";
import type { FieldPatch } from "@/hooks/usePatchArtifact.ts";
import { routineQueries, useRoutineMutations } from "@/hooks/useRoutines.ts";
import { threadQueries, updateThreadToInProgress } from "@/hooks/useThreads.tsx";
import type { ArtifactAction, RoutineArtifactResp } from "@/types/artifacts.ts";
import { artifactDisplayTitle } from "@/shared/artifactTitle.ts";
import type {
  ArtifactComponent,
  ArtifactView as ArtifactViewSchema,
  LeafComponent,
  ViewParticipant,
} from "@/types/artifacts-view.ts";
import type { RoutineResp, TriggerConfig, UpdateRoutineReq } from "@/types/routines.ts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo, useRef, useState } from "react";
import { CanvasModalCard } from "./CanvasModalCard.tsx";

/**
 * The canvas card for a routine artifact that has already been approved. An
 * approved routine is a "final" status, so the generic {@link CanvasArtifactModal}
 * renders it read-only and the server builds its view with every field
 * `editable: false`. A user still needs to tweak a live routine (its schedule,
 * prompt, or permissions) after approving it, so this variant re-enables editing
 * client-side and defers the write behind an explicit Save button instead of the
 * per-field blur-save the pre-approval card uses.
 *
 * Approval is the point where the routine stops living in the artifact. From then
 * on the backing routine (a user ent, or a system routine the user is tracking) is
 * what actually runs, and the artifact is a frozen snapshot of what was approved.
 * So this card reads *and* writes the routine, not the artifact: the artifact's
 * view supplies the layout, the routine supplies the values, and Save goes to the
 * routines API — which is also what re-syncs the cron schedule. Patching the
 * artifact instead would be rejected (it is past `waitingForUserAction`) and, if
 * it weren't, would change only the snapshot while the routine kept running its
 * old config.
 *
 * The footer keeps the routine's server-provided "Run now" action visible by
 * default; a Save button joins it only once there are pending edits. Edits are
 * buffered locally (the view context's patcher is overridden to accumulate
 * `{ path, value }` pairs rather than POST each one) and overlaid on top of the
 * live values so the blocks reflect them immediately.
 */
export const RoutineArtifactCanvasCard = ({
  artifact,
  view,
  threadId,
  isSharedThread,
  userIsAdmin,
  container,
  isFullscreen,
  showBack,
  onBack,
  onClose,
}: {
  artifact: RoutineArtifactResp;
  view: ArtifactViewSchema;
  threadId: number;
  isSharedThread: boolean;
  userIsAdmin: boolean;
  /** This layer's wrapper, which scopes the card and scrim to the canvas. */
  container: HTMLElement;
  /** Whether the presented entity is shown fullscreen. */
  isFullscreen: boolean;
  showBack: boolean;
  onBack: () => void;
  onClose: () => void;
}) => {
  const queryClient = useQueryClient();
  const { updateRoutine, editSystemRoutine } = useRoutineMutations();

  // Detecting deletion is subtle. Deleting a routine removes the backing `routine`
  // ent but leaves this `routineArtifact` ent untouched — `mapRoutineArtifact`
  // reads status off the artifact's own data, so it keeps returning "approved" and
  // the by-id fetch never 404s. The authoritative existence signal is the routines
  // list: a deleted routine drops out of it. The list is also the only signal that
  // covers the artifacts with no ent of their own to probe (see `backingRoutine`),
  // and it's the cache the routines UI already invalidates on delete, so an
  // in-session delete lands here at once. Poll it alongside the by-id query, which
  // catches artifact-level changes (hard delete → 404, or a status flip) and lets
  // CanvasArtifactModal swap in its "couldn't be loaded" / read-only card.
  const { data: liveData, isError: artifactGone } = useQuery({
    ...artifactQueries.byId(artifact.id),
    refetchInterval: 5000,
  });
  const { data: routines } = useQuery({
    ...routineQueries.list(),
    refetchInterval: 15000,
  });

  // The list row backing this artifact: its own routine ent, or the system routine
  // it was approved from. The fallback isn't a nicety — an artifact approved off a
  // system routine carries no `routineEntId` at all, since the edit landed as a
  // preference override rather than as an ent.
  const backingRoutine = useMemo(
    () => (routines ? resolveBackingRoutine(routines, artifact) : undefined),
    [routines, artifact],
  );

  const liveIsApprovedRoutine =
    liveData?.isHeavy === true &&
    liveData.artifactType === "routineArtifact" &&
    liveData.status === "approved";

  // The routine can no longer be edited once it drops out of the list or its
  // artifact is gone, or its status moves off "approved". Conclude this only from
  // positive signals (a fetch error, or a resolved-but-changed response) so the
  // form isn't blocked during the initial existence check — `liveData` is served
  // from the cache the parent already populated, while the list may still be in
  // flight.
  const routineDeleted =
    artifactGone ||
    (routines !== undefined && backingRoutine === undefined) ||
    (liveData !== undefined && !liveIsApprovedRoutine);
  const routineEditable = !routineDeleted;

  // Path → latest edited value. Buffered instead of written on every blur so an
  // already-live routine only changes when the user commits with Save. `pending`
  // drives rendering; `pendingRef` mirrors it and is written synchronously in
  // `bufferPatches`, so a blur-to-save field committing during the Save click
  // (its handler runs before `handleSave`, before React flushes the state) is
  // still seen by the save rather than dropped.
  const [pending, setPending] = useState<Map<string, unknown>>(() => new Map());
  const pendingRef = useRef(pending);
  // Monotonic write counter plus the counter value at which each path was last
  // edited. A save records the counters for the paths it sends; on success it
  // clears only paths whose counter is unchanged. Any edit that lands after the
  // capture — a late blur, an already-scheduled callback firing mid-save — bumps
  // the counter, so it survives the clear rather than being silently dropped.
  const seqRef = useRef(0);
  const pathSeqRef = useRef(new Map<string, number>());

  const saveMutation = useMutation({
    mutationFn: async ({ routine, update }: { routine: RoutineResp; update: UpdateRoutineReq }) => {
      // A routine okthx ships has no ent, and its prompt is not the user's to
      // replace — the server drops one that arrives anyway. Send the amendment
      // instead, and never the prompt. Both helpers invalidate the routines
      // cache, so the card re-renders from the saved values.
      if (routine.kind === "system" && routine.systemRoutineId) {
        const { prompt: _okthxOwnsThis, ...rest } = update;
        return await editSystemRoutine(routine.systemRoutineId, {
          ...rest,
          amendment: update.amendment ?? routine.amendment ?? "",
        });
      }
      return await updateRoutine(routine.id, update);
    },
    onError: (error) => {
      console.error(error);
      errorToast("Error saving routine");
    },
  });

  // True for the whole save — the pre-save refetch plus the mutation — not just
  // the mutation (which `saveMutation.isPending` wouldn't cover). The view goes
  // read-only while it's set, so a user can't start a fresh edit mid-save; the
  // per-path sequence check on clear handles anything that still slips through
  // (a late blur or an already-scheduled callback).
  const [isSaving, setIsSaving] = useState(false);

  const bufferPatches = useCallback((patches: FieldPatch[]) => {
    const next = new Map(pendingRef.current);
    for (const p of patches) {
      next.set(p.path, p.value);
      pathSeqRef.current.set(p.path, ++seqRef.current);
    }
    // Keep the ref and the state in lockstep: the ref updates now (so a Save that
    // immediately follows sees this edit), the state schedules the re-render.
    pendingRef.current = next;
    setPending(next);
    return Promise.resolve();
  }, []);

  // Blocks call `patch`/`patchOne` from the view context; route those into the
  // local buffer instead of the network so nothing persists until Save.
  const patcherOverride = useMemo<ArtifactViewPatcherOverride>(
    () => ({
      patch: (patches) => bufferPatches(patches),
      patchOne: (path, value) => bufferPatches([{ path, value }]),
      isPending: isSaving,
    }),
    [bufferPatches, isSaving],
  );

  // Re-enable editing (the server flags the whole view non-editable once
  // approved) and overlay the live routine values plus any buffered edits, so the
  // fields show what runs today rather than what was approved. Once the routine is
  // gone the fields flip back to read-only, blocking further edits.
  const editableView = useMemo(
    () =>
      toEditableView(
        hideSystemOwnedFields(view, isSystemRoutineArtifact(artifact)),
        backingRoutine,
        pending,
        routineEditable,
      ),
    [view, artifact, backingRoutine, pending, routineEditable],
  );
  const isDirty = pending.size > 0;

  const handleSave = useCallback(async () => {
    // Read the buffer from the ref, not state: a blur-to-save field committing
    // as part of the Save click runs before React flushes that edit into
    // `pending`, but `pendingRef` already has it.
    const saved = pendingRef.current;
    // Don't write to a routine that no longer exists (or is mid-deletion): the
    // save would 404. The Save button is already hidden in this state; this
    // guards a stale click that raced the deletion. `isSaving` blocks a
    // double-submit.
    if (saved.size === 0 || !routineEditable || !backingRoutine || isSaving) return;
    // Sequence of each path as sent, so the post-save clear can tell an untouched
    // entry from one edited again while the request was in flight.
    const savedSeq = new Map<string, number>();
    for (const path of saved.keys()) savedSeq.set(path, pathSeqRef.current.get(path) ?? 0);
    setIsSaving(true);
    try {
      // `permissions` and `triggerConfig` save as whole objects, so building the
      // payload off a poll snapshot (up to 15s stale) would let saving one field
      // clobber a sibling another surface just changed. Read the freshest routines
      // first and merge onto that.
      //
      // Pin the target to the exact row the card resolved (by id), not a
      // re-resolution: if that routine was deleted meanwhile it's simply gone
      // from the list, so abort. Re-resolving could match the source system row
      // and route the buffered edits onto a different routine than the one the
      // user was editing.
      const fresh = await queryClient.fetchQuery(routineQueries.list());
      const target = fresh.find((routine) => routine.id === backingRoutine.id);
      if (!target) return;
      await saveMutation.mutateAsync({
        routine: target,
        update: buildRoutineUpdate(target, saved),
      });
      // Clear only the paths we saved that haven't been edited since — a later
      // edit bumped the path's sequence, so it stays buffered for the next Save.
      const next = new Map(pendingRef.current);
      for (const path of saved.keys()) {
        if (pathSeqRef.current.get(path) === savedSeq.get(path)) {
          next.delete(path);
          pathSeqRef.current.delete(path);
        }
      }
      pendingRef.current = next;
      setPending(next);
    } catch {
      // The mutation's onError surfaces the toast; keep the buffer so the user
      // can retry without re-entering their edits.
    } finally {
      setIsSaving(false);
    }
  }, [routineEditable, backingRoutine, isSaving, saveMutation, queryClient]);

  // "Run now" (the routine's server-provided action) fires like it does in the
  // default footer: flip the thread in-progress optimistically and close the
  // canvas, rolling the thread cache back if the POST fails.
  const actions = "actions" in artifact && artifact.actions ? artifact.actions : [];
  const handleActionFire = useCallback(
    (action: ArtifactAction) => {
      updateThreadToInProgress(queryClient, threadId, action.pendingBody);
      onClose();
    },
    [queryClient, threadId, onClose],
  );
  const handleActionError = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: threadQueries.all() });
  }, [queryClient]);

  return (
    <CanvasModalCard
      title={artifactDisplayTitle(artifact)}
      container={container}
      isFullscreen={isFullscreen}
      showBack={showBack}
      onBack={onBack}
      onClose={onClose}
      footer={
        <>
          <div className="text-xs">
            <ArtifactStatusDate artifact={artifact} />
          </div>
          <div className="flex items-center gap-2">
            {isDirty && routineEditable && backingRoutine ? (
              <Button
                size="sm"
                onClick={handleSave}
                disabled={isSaving}
                data-statsig-name="routine-artifact-save"
              >
                {isSaving ? "Saving…" : "Save"}
              </Button>
            ) : null}
            {routineEditable && actions.length > 0 ? (
              <ArtifactActionsButtons
                actions={actions}
                layout="flat"
                onActionFire={handleActionFire}
                onActionError={handleActionError}
                // Edits sit in `pending` until Save writes them to the routine,
                // and "Run now" fires the stored routine — so running while
                // dirty would run the version the user just edited away from,
                // with nothing on screen saying so (BIG-6261). Save is rendered
                // right beside it whenever this is true, so the way forward is
                // visible rather than just blocked.
                disabled={isDirty || isSaving}
              />
            ) : null}
          </div>
        </>
      }
    >
      {routineDeleted ? (
        <output className="mx-4 mt-4 block rounded-md border border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
          This routine is no longer available and can no longer be edited or run.
        </output>
      ) : null}
      <ArtifactErrorBoundary
        artifact={artifact}
        threadId={threadId}
        messageId={artifact.messageId}
        userIsAdmin={userIsAdmin}
      >
        <ArtifactView
          view={editableView}
          threadId={threadId}
          messageId={artifact.messageId}
          artifactId={artifact.id}
          isReadOnly={!routineEditable || isSaving}
          isSharedThread={isSharedThread}
          maximized
          onRequestClose={onClose}
          patcherOverride={patcherOverride}
        />
      </ArtifactErrorBoundary>
    </CanvasModalCard>
  );
};

/**
 * Resolve the list row backing an approved routine artifact.
 *
 * An artifact bound to its own ent (`routineEntId` set) resolves to *only* that
 * ent — deliberately no fallback to the source system row. If the ent is gone
 * the card is stale and reads as deleted; falling back would silently move the
 * user's buffered edits onto the shipped routine instead.
 *
 * The fallback is correct only for a preference-lane approval (`routineEntId`
 * empty): it never had an ent, so the system routine genuinely *is* what it edits.
 */
const resolveBackingRoutine = (
  routines: readonly RoutineResp[],
  artifact: Pick<RoutineArtifactResp, "routineEntId" | "sourceSystemRoutineId">,
): RoutineResp | undefined => {
  if (artifact.routineEntId) {
    return routines.find((routine) => routine.id === artifact.routineEntId);
  }
  return artifact.sourceSystemRoutineId
    ? routines.find((routine) => routine.systemRoutineId === artifact.sourceSystemRoutineId)
    : undefined;
};

/** Prefix every trigger-config write path carries in the artifact view. */
const TRIGGER_PREFIX = "triggerConfig.";

/**
 * Read a value the email-filter blocks work in: a list of bare addresses. The
 * routine stores these as `string[]`, except `organizer`, which is a single
 * string. Returns undefined for anything else, so the caller falls back.
 */
const toEmailList = (value: unknown): string[] | undefined => {
  if (typeof value === "string") return value ? [value] : [];
  if (Array.isArray(value) && value.every((entry) => typeof entry === "string")) return value;
  return undefined;
};

const emailParticipant = (email: string): ViewParticipant => ({
  id: email,
  name: email,
  value: email,
  type: "emailAddress",
});

/**
 * The value the live routine holds for a view block's `editPath`, or undefined
 * for a path the routine doesn't carry. The view's paths are flat while the
 * routine nests permissions and trigger settings, so the mapping is explicit.
 */
const liveValueFor = (path: string, routine: RoutineResp): unknown => {
  switch (path) {
    case "name":
      return routine.name;
    case "description":
      // The view block renders "" for a routine with no description, so match
      // that here — undefined would read as "this path isn't on the routine".
      return routine.description ?? "";
    case "prompt":
      return routine.prompt;
    case "amendment":
      return routine.amendment ?? "";
    case "enabled":
      return routine.enabled;
    case "autoSendCommunication":
      return routine.permissions.autoSendCommunication;
    case "autoArchive":
      return routine.permissions.autoArchive;
    case "defaultFrom":
      return routine.permissions.defaultFrom;
    case "triggerType":
      return routine.triggerConfig.type;
    default: {
      if (!path.startsWith(TRIGGER_PREFIX)) return undefined;
      const config: Record<string, unknown> = routine.triggerConfig;
      return config[path.slice(TRIGGER_PREFIX.length)];
    }
  }
};

/**
 * Fold the buffered edits into an update for the routines API. `permissions` and
 * `triggerConfig` are whole-object fields, so a single toggle has to be merged
 * over the routine's current values rather than sent alone — otherwise saving
 * "auto-archive" would clear the sibling permissions.
 */
const buildRoutineUpdate = (routine: RoutineResp, pending: Map<string, unknown>) => {
  const update: UpdateRoutineReq = {};
  const permissions = { ...routine.permissions };
  const triggerConfig: Record<string, unknown> = { ...routine.triggerConfig };
  let permissionsEdited = false;
  let triggerEdited = false;

  for (const [path, value] of pending) {
    switch (path) {
      case "name":
        if (typeof value === "string") update.name = value;
        break;
      case "description":
        // Trimmed, and sent even when empty: JSON drops an undefined key, so a
        // cleared description would never reach the server. It normalizes "" to
        // undefined on the way to storage.
        if (typeof value === "string") update.description = value.trim();
        break;
      case "prompt":
        if (typeof value === "string") update.prompt = value;
        break;
      case "amendment":
        if (typeof value === "string") update.amendment = value;
        break;
      case "enabled":
        if (typeof value === "boolean") update.enabled = value;
        break;
      case "autoSendCommunication":
        if (typeof value === "boolean") {
          permissions.autoSendCommunication = value;
          permissionsEdited = true;
        }
        break;
      case "autoArchive":
        if (typeof value === "boolean") {
          permissions.autoArchive = value;
          permissionsEdited = true;
        }
        break;
      case "defaultFrom":
        if (value === "user" || value === "assistant") {
          permissions.defaultFrom = value;
          permissionsEdited = true;
        }
        break;
      default:
        if (path.startsWith(TRIGGER_PREFIX)) {
          const key = path.slice(TRIGGER_PREFIX.length);
          // The organizer filter renders as a single-cardinality participant
          // list, so the picker hands back a one-element array for a field the
          // routine stores as a bare string.
          triggerConfig[key] = key === "organizer" ? toEmailList(value)?.[0] : value;
          triggerEdited = true;
        }
        break;
    }
  }

  if (permissionsEdited) update.permissions = permissions;
  if (triggerEdited) {
    // Safe to assert: the trigger *type* is not editable here (see
    // `NON_EDITABLE_PATHS`), so the discriminant is untouched and only fields
    // belonging to that type were merged in. The server re-validates regardless.
    update.triggerConfig = triggerConfig as TriggerConfig;
  }
  return update;
};

/**
 * Paths this card renders read-only even while the routine is live. The set of
 * trigger fields shown is baked into the server-built view from the artifact's
 * trigger type, and this card never re-fetches that view — so switching the
 * trigger type here would leave the previous type's fields on screen and produce
 * a config the routine's fields don't match. Changing the trigger type is a
 * routine-settings operation.
 */
const NON_EDITABLE_PATHS = new Set(["triggerType"]);

/** The write paths of the fields a routine okthx ships doesn't own. */
const SYSTEM_OWNED_PATHS = new Set(["prompt", "triggerType"]);

/**
 * Whether this card is showing a routine okthx ships — read from the artifact,
 * not from the resolved routine row.
 *
 * The row comes from a list query that is in flight on first render and absent
 * for good once the routine is deleted, and the card deliberately renders through
 * both (see `routineDeleted`). Keying the narrowing below to it would put okthx's
 * prompt back on screen for the whole of that first paint, and leave it there on
 * a deleted routine. The artifact answers synchronously, and it is the same field
 * the server builds the view from.
 *
 * The `routineEntId` precedence mirrors `resolveBackingRoutine`, so the two can't
 * disagree about which routine this card is for: an artifact naming an ent is
 * backed by that ent whatever it was first loaded from, and a routine of the
 * user's own owns its prompt outright.
 */
const isSystemRoutineArtifact = (
  artifact: Pick<RoutineArtifactResp, "routineEntId" | "sourceSystemRoutineId">,
): boolean => !artifact.routineEntId && artifact.sourceSystemRoutineId !== undefined;

const editPathOf = (component: ArtifactComponent | LeafComponent): string | undefined =>
  "editPath" in component ? component.editPath : undefined;

const isSystemOwned = (component: ArtifactComponent | LeafComponent | undefined): boolean => {
  const editPath = component && editPathOf(component);
  return editPath !== undefined && SYSTEM_OWNED_PATHS.has(editPath);
};

/**
 * Drop the fields a routine okthx ships doesn't own, rather than showing them
 * read-only.
 *
 * `pathIsLocked` already stops both from being written, but read-only is the
 * wrong shape for either. The prompt is okthx's text, kept up to date upstream —
 * the user can neither change it nor act on it, and at the length these run it
 * is most of the card (BIG-7468). A locked trigger type still renders as a
 * disabled dropdown, so it reads as a control that just won't open. What has a
 * per-user override behind it stays: the schedule, the description, and their
 * own instructions, which is where a shipped routine bends to the user.
 *
 * The same narrowing the routines settings screen does through
 * `SystemRoutineFields`, applied to the server-built view this card renders.
 */
const hideSystemOwnedFields = (
  view: ArtifactViewSchema,
  isSystemRoutine: boolean,
): ArtifactViewSchema => {
  if (!isSystemRoutine) return view;
  return {
    ...view,
    components: view.components
      .filter(
        (component, index) =>
          !isSystemOwned(component) &&
          // A section label captions the component that follows it, so "Then do"
          // goes wherever the prompt goes. Left behind it would caption the
          // user's own instructions below.
          !(component.type === "sectionLabel" && isSystemOwned(view.components[index + 1])),
      )
      // The trigger type sits beside the "When" caption inside a group, which
      // then renders as the caption alone — the heading over a stated trigger.
      .map((component) =>
        component.type === "horizontalGroup" || component.type === "card"
          ? { ...component, children: component.children.filter((c) => !isSystemOwned(c)) }
          : component,
      ),
  };
};

/**
 * Whether this block stays read-only even while the routine is live.
 *
 * `prompt` is conditional rather than a member of the set above: a user routine
 * owns its prompt outright, but a system routine's is okthx's text and the save
 * path strips it. Re-enabling it there would let the user type into a field
 * whose contents are dropped on the way out — the save succeeds, the buffer
 * clears as saved, and the text snaps back with nothing saying why. Their own
 * instructions go in the amendment block below it.
 *
 * `hideSystemOwnedFields` drops that block from a system routine's card
 * outright, so this clause is what holds the invariant if the field ever renders
 * again. The two share `SYSTEM_OWNED_PATHS` but read the routine's identity from
 * different places, deliberately: what the card *shows* can't wait on a list
 * query that is still in flight, while a *write* can only happen once that query
 * has resolved — `handleSave` returns early without a `backingRoutine` — so here
 * the row is always the one the save will actually go to.
 */
const pathIsLocked = (editPath: string | undefined, routine: RoutineResp | undefined): boolean => {
  if (editPath === undefined) return false;
  if (NON_EDITABLE_PATHS.has(editPath)) return true;
  return routine?.kind === "system" && SYSTEM_OWNED_PATHS.has(editPath);
};

/**
 * Set every editable-capable block's `editable` flag to `editable` and rebind its
 * value: the live routine's value where it has one (the artifact snapshot froze at
 * approval and would otherwise show stale text), then any buffered edit on top.
 * While the routine is live this re-enables editing (the server disables the whole
 * view once approved); once it's gone the `false` flag — alongside the view's
 * `isReadOnly` — blocks the form. Recurses into the two container types so fields
 * nested in a card/group are covered.
 */
const toEditableView = (
  view: ArtifactViewSchema,
  routine: RoutineResp | undefined,
  pending: Map<string, unknown>,
  editable: boolean,
): ArtifactViewSchema => ({
  ...view,
  components: view.components.map((component) =>
    prepareComponent(component, routine, pending, editable),
  ),
});

const prepareComponent = (
  component: ArtifactComponent,
  routine: RoutineResp | undefined,
  pending: Map<string, unknown>,
  editable: boolean,
): ArtifactComponent => {
  if (component.type === "horizontalGroup" || component.type === "card") {
    return {
      ...component,
      children: component.children.map((child) => prepareLeaf(child, routine, pending, editable)),
    };
  }
  // Not a leaf, and nothing a routine card ever contains — each owns its own
  // behaviour rather than reading a routine field (one submits a value, the
  // other mints a link), so there is nothing here to overlay onto it.
  if (component.type === "secretInput" || component.type === "computerDesktop") {
    return component;
  }
  return prepareLeaf(component, routine, pending, editable);
};

/** Buffered edit if there is one, else the live routine's value. */
const overlayFor = (
  path: string | undefined,
  routine: RoutineResp | undefined,
  pending: Map<string, unknown>,
): unknown => {
  if (!path) return undefined;
  if (pending.has(path)) return pending.get(path);
  return routine ? liveValueFor(path, routine) : undefined;
};

const prepareLeaf = (
  component: LeafComponent,
  routine: RoutineResp | undefined,
  pending: Map<string, unknown>,
  editable: boolean,
): LeafComponent => {
  const editPath = editPathOf(component);
  const fieldEditable = editable && !pathIsLocked(editPath, routine);
  const overlay = overlayFor(editPath, routine, pending);

  switch (component.type) {
    case "text":
    case "markdown":
    case "location":
    case "select": {
      const value = typeof overlay === "string" ? overlay : component.value;
      return { ...component, editable: fieldEditable, value };
    }
    case "cronSchedule": {
      const value = typeof overlay === "string" ? overlay : component.value;
      // The timezone rides along with the schedule in the routine's trigger
      // config, and the picker needs the live one to render the right clock.
      const liveTimezone =
        routine?.triggerConfig.type === "cron" ? routine.triggerConfig.timezone : undefined;
      return {
        ...component,
        editable: fieldEditable,
        value,
        timezone: liveTimezone ?? component.timezone,
      };
    }
    case "toggle": {
      const value = typeof overlay === "boolean" ? overlay : component.value;
      return { ...component, editable: fieldEditable, value };
    }
    case "numberInput": {
      const value = typeof overlay === "number" || overlay === null ? overlay : component.value;
      return { ...component, editable: fieldEditable, value };
    }
    case "participantList": {
      // Email-filter lists (fromSenders, organizer, …) carry bare addresses, so
      // the pills can be rebuilt from the live value. Handle-backed lists resolve
      // contacts through ids we can't synthesize — those keep what the server
      // rendered.
      const emails = component.participantSource === "email" ? toEmailList(overlay) : undefined;
      return {
        ...component,
        editable: fieldEditable,
        participants: emails ? emails.map(emailParticipant) : component.participants,
      };
    }
    // Editable-capable blocks whose value binding isn't a single scalar we can
    // safely overlay — they manage their own draft state, so setting `editable`
    // is enough; the buffered edit still saves.
    case "emailRecipients":
    case "emailAttachments":
    case "dateTimeRange":
    case "recurrence":
    case "reminders":
    case "schedulingMadlibs":
    case "kickoffBodyWithSlots":
      return { ...component, editable: fieldEditable };
    default:
      return component;
  }
};
